import React, { useCallback, useContext, useMemo } from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import ListItem, { ListItemProps } from '@material-ui/core/ListItem';
import classnames from 'classnames';
import { compact, concat, filter, includes } from 'lodash';

import { Checkbox } from 'components/v2';

import ListNodesContext from './ListNodesContext';
import { Audit } from './types/auditType';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    buttonWithoutHover: {
      '&:hover': {
        backgroundColor: 'initial',
      },
      '&:focus': {
        backgroundColor: 'initial',
      },
    },
    checkboxRoot: {
      paddingTop: 3,
      paddingBottom: 3,
      marginLeft: -1 * unit * 2.5,
    },
  });

export interface ListItemNodeAuditsProps extends Omit<ListItemProps, 'classes'>, WithStyles<typeof styles> {
  node: Audit;
  disabled?: boolean;
}

const ListItemNodeAudits: React.FC<ListItemNodeAuditsProps> = ({
  classes,
  className,
  node,
  children,
  onClick,
  disabled = false,
  ...rest
}) => {
  const { idsSelected, idsChecked, onNodeSelected, onNodesChecked } = useContext(ListNodesContext);

  const selected = useMemo(() => includes(idsSelected, node._id), [idsSelected, node._id]);

  const checked = useMemo(() => includes(idsChecked, node._id), [idsChecked, node._id]);

  const handleNodeChecked = useCallback(
    (event: React.UIEvent | React.SyntheticEvent) => {
      if (!onNodesChecked) {
        return;
      }
      const newIdsChecked = checked ? filter(idsChecked, (_id) => _id !== node._id) : concat(idsChecked, node._id);
      onNodesChecked(event, compact(newIdsChecked));
    },

    [checked, idsChecked, node._id, onNodesChecked],
  );

  return (
    <ListItem
      disabled={disabled}
      classes={{
        root: className,
        button: classnames({
          [classes.buttonWithoutHover]: onNodesChecked && !onNodeSelected,
        }),
      }}
      button={!!onNodeSelected || !!onNodesChecked}
      selected={selected}
      onClick={(event) => {
        if (onClick) {
          onClick(event);
        }
        if (onNodeSelected) {
          onNodeSelected(event, node);
        } else if (onNodesChecked) {
          handleNodeChecked(event);
        }
      }}
      {...rest}
    >
      {onNodesChecked && (
        <Checkbox
          disabled={disabled}
          classes={{ root: classes.checkboxRoot }}
          color="primary"
          size="small"
          tabIndex={-1}
          disableRipple
          checked={checked}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => handleNodeChecked(event)}
          inputProps={{
            'data-lh-id': 'filter-facilities-list-item-node-checkbox',
          }}
        />
      )}
      {children}
    </ListItem>
  );
};

export default withStyles(styles)(ListItemNodeAudits);
